import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Banner, Button, Card, EmptyState, Field, Input, Select, Table, Td, Th } from "@magarac/ui";
import {
  createJobTypeInputSchema,
  jobTypeListSchema,
  jobTypeResponseSchema,
  type JobType,
} from "@magarac/shared/schemas/jobType";
import { HelpIcon } from "../../components/HelpIcon";
import { api } from "../../lib/api";
import { messageFor } from "../../lib/errors";
import { useTenant } from "../../lib/tenant";

export const Route = createFileRoute("/_app/services")({
  component: ServicesPage,
});

interface Draft {
  name: string;
  durationMin: number;
  windowMin: number;
  bufferMin: number;
  crewSize: number;
  bookableOnline: boolean;
}

const blank: Draft = {
  name: "",
  durationMin: 60,
  windowMin: 120,
  bufferMin: 15,
  crewSize: 1,
  bookableOnline: true,
};

/** Services = job types (CRM-11, SCH-3). Screen keys: services.list, services.edit */
function ServicesPage() {
  const { active } = useTenant();
  const queryClient = useQueryClient();
  const mode =
    active?.industryTemplate === "salon" || active?.industryTemplate === "auto_repair"
      ? "appointment"
      : "field";
  const list = useQuery({
    queryKey: ["job-types", active?.id],
    queryFn: () => api.get("/job-types", jobTypeListSchema, { query: { includeInactive: true } }),
    enabled: Boolean(active),
  });
  const [draft, setDraft] = useState<Draft>({ ...blank, windowMin: mode === "field" ? 120 : 0 });
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["job-types", active?.id] });

  const save = useMutation({
    mutationFn: async () => {
      const input = { ...draft, mode, windowMin: mode === "appointment" ? 0 : draft.windowMin };
      const parsed = createJobTypeInputSchema.safeParse(input);
      if (!parsed.success)
        throw new Error("Please check the values: name and duration are required.");
      return editing
        ? api.patch(`/job-types/${editing}`, parsed.data, jobTypeResponseSchema)
        : api.post("/job-types", parsed.data, jobTypeResponseSchema);
    },
    onSuccess: async () => {
      setDraft({ ...blank, windowMin: mode === "field" ? 120 : 0 });
      setEditing(null);
      setError(null);
      await refresh();
    },
    onError: (err) => setError(messageFor(err)),
  });
  const toggle = useMutation({
    mutationFn: (jt: JobType) =>
      api.patch(`/job-types/${jt.id}`, { active: !jt.active }, jobTypeResponseSchema),
    onSuccess: refresh,
  });

  const startEdit = (jt: JobType) => {
    setEditing(jt.id);
    setDraft({
      name: jt.name,
      durationMin: jt.durationMin,
      windowMin: jt.windowMin,
      bufferMin: jt.bufferMin,
      crewSize: jt.crewSize,
      bookableOnline: jt.bookableOnline,
    });
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Services</h1>
        <HelpIcon screen="services.list" />
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}

      <Card title={editing ? "Edit service" : "Add a service"} className="mb-6">
        <div className="grid gap-2 md:grid-cols-2">
          <Field id="svc-name" label="Name">
            <Input
              id="svc-name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="AC tune-up"
            />
          </Field>
          <Field id="svc-duration" label="How long it takes (minutes)">
            <Input
              id="svc-duration"
              type="number"
              inputMode="numeric"
              min={5}
              step={5}
              value={draft.durationMin}
              onChange={(e) => setDraft({ ...draft, durationMin: Number(e.target.value) })}
            />
          </Field>
          {mode === "field" ? (
            <Field
              id="svc-window"
              label="Arrival window (minutes)"
              hint="0 means an exact time. 120 shows customers a two-hour window."
            >
              <Input
                id="svc-window"
                type="number"
                inputMode="numeric"
                min={0}
                step={30}
                value={draft.windowMin}
                onChange={(e) => setDraft({ ...draft, windowMin: Number(e.target.value) })}
              />
            </Field>
          ) : null}
          <Field
            id="svc-buffer"
            label="Buffer after (minutes)"
            hint="Cleanup, paperwork, a breather."
          >
            <Input
              id="svc-buffer"
              type="number"
              inputMode="numeric"
              min={0}
              step={5}
              value={draft.bufferMin}
              onChange={(e) => setDraft({ ...draft, bufferMin: Number(e.target.value) })}
            />
          </Field>
          <Field id="svc-crew" label="People needed">
            <Input
              id="svc-crew"
              type="number"
              inputMode="numeric"
              min={1}
              max={10}
              value={draft.crewSize}
              onChange={(e) => setDraft({ ...draft, crewSize: Number(e.target.value) })}
            />
          </Field>
          <Field id="svc-online" label="Customers can book this online">
            <Select
              id="svc-online"
              value={draft.bookableOnline ? "yes" : "no"}
              onChange={(e) => setDraft({ ...draft, bookableOnline: e.target.value === "yes" })}
            >
              <option value="yes">Yes</option>
              <option value="no">No, office only</option>
            </Select>
          </Field>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => save.mutate()} busy={save.isPending} disabled={!draft.name}>
            {editing ? "Save changes" : "Add service"}
          </Button>
          {editing ? (
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(null);
                setDraft({ ...blank, windowMin: mode === "field" ? 120 : 0 });
              }}
            >
              Cancel
            </Button>
          ) : null}
        </div>
      </Card>

      <Card title="Your services">
        {list.data && list.data.jobTypes.length === 0 ? (
          <EmptyState title="No services yet" body="Add the work you do so it can be booked." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Service</Th>
                <Th>Length</Th>
                {mode === "field" ? <Th>Window</Th> : null}
                <Th>People</Th>
                <Th>Online</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {list.data?.jobTypes.map((jt) => (
                <tr key={jt.id} className={jt.active ? "" : "opacity-60"}>
                  <Td>
                    <div className="font-medium">{jt.name}</div>
                    {jt.description ? (
                      <div className="text-xs text-muted">{jt.description}</div>
                    ) : null}
                  </Td>
                  <Td>{jt.durationMin} min</Td>
                  {mode === "field" ? (
                    <Td>{jt.windowMin ? `${jt.windowMin} min` : "Exact time"}</Td>
                  ) : null}
                  <Td>{jt.crewSize}</Td>
                  <Td>{jt.bookableOnline ? "Yes" : "No"}</Td>
                  <Td>
                    <div className="flex gap-2">
                      <Button variant="secondary" onClick={() => startEdit(jt)}>
                        Edit
                      </Button>
                      <Button variant="ghost" onClick={() => toggle.mutate(jt)}>
                        {jt.active ? "Hide" : "Show"}
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
